import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, AlertCircle, CheckCircle, Check, KeyRound } from 'lucide-react';
import { api } from '../../services/api';
import SEO from '../../components/common/SEO';
import { useWebsiteContentOptional } from '../../contexts/WebsiteContentContext';
import { useLanguage } from '../../contexts/LanguageContext';
import EditableElement from '../../components/admin/EditableElement';
import { Turnstile } from '../../components/Turnstile';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const content = useWebsiteContentOptional();
  const { t } = useLanguage();

  // Helper to get content with fallback (CMS > i18n > hardcoded)
  const get = (key: string, fallback: string, i18nKey?: string) => {
    const translatedFallback = i18nKey ? t(i18nKey, fallback) : fallback;
    return content?.getContent('auth', key, translatedFallback) ?? translatedFallback;
  };

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';
  const [turnstileToken, setTurnstileToken] = useState('');

  const passwordRequirements = [
    { met: password.length >= 8, text: get('password_req_length', 'At least 8 characters') },
    { met: /[A-Z]/.test(password), text: get('password_req_uppercase', 'One uppercase letter') },
    { met: /[a-z]/.test(password), text: get('password_req_lowercase', 'One lowercase letter') },
    { met: /[0-9]/.test(password), text: get('password_req_number', 'One number') },
  ];

  const allRequirementsMet = passwordRequirements.every((req) => req.met);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError(get('error_invalid_token', 'Invalid or missing reset token'));
      return;
    }

    if (!allRequirementsMet) {
      setError(get('error_password_requirements', 'Please meet all password requirements'));
      return;
    }

    if (!passwordsMatch) {
      setError(get('error_passwords_not_match', 'Passwords do not match'));
      return;
    }

    setIsLoading(true);

    try {
      await api.resetPassword(token, password);
      setSuccess(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : get('error_reset_failed', 'Failed to reset password'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <>
        <SEO page="resetPassword" />
        <div className="min-h-screen bg-gray-50">
          {/* Hero Section */}
          <div className="relative overflow-hidden bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl opacity-10" />
              <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-accent rounded-full blur-3xl opacity-15" />
            </div>
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-32 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 backdrop-blur-sm rounded-full text-sm font-medium text-white mb-6 border border-red-400/30 animate-fade-in">
                <AlertCircle className="w-4 h-4" />
                {t('resetPassword.invalidBadge', 'Invalid Link')}
              </div>
              <EditableElement section="auth" field="invalid_link_title">
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white animate-slide-up">
                  {get('invalid_link_title', 'Invalid reset', 'resetPassword.invalidTitle')}{' '}
                  <span className="text-accent">{get('invalid_link_title_highlight', 'link', 'resetPassword.invalidTitleHighlight')}</span>
                </h1>
              </EditableElement>
              <EditableElement section="auth" field="invalid_link_message">
                <p className="mt-4 text-lg text-primary-100 max-w-2xl mx-auto animate-slide-up animation-delay-100">
                  {get('invalid_link_message', 'This password reset link is invalid or has expired.', 'resetPassword.invalidMessage')}
                </p>
              </EditableElement>
            </div>
            <div className="absolute bottom-0 left-0 right-0">
              <svg viewBox="0 0 1440 80" fill="none" className="w-full h-auto">
                <path d="M0 80L60 70C120 60 240 40 360 35C480 30 600 40 720 45C840 50 960 50 1080 45C1200 40 1320 30 1380 25L1440 20V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0Z" fill="#f9fafb" />
              </svg>
            </div>
          </div>

          {/* Card */}
          <div className="relative -mt-24 pb-16">
            <div className="max-w-md mx-auto px-4">
              <div className="bg-white rounded-2xl shadow-2xl p-8 text-center animate-fade-in animation-delay-200">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <p className="mt-4 text-gray-600">
                  {get('invalid_link_help', 'Please request a new password reset link to continue.', 'resetPassword.invalidHelp')}
                </p>
                <Link
                  to="/forgot-password"
                  className="mt-6 inline-block px-6 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors"
                >
                  {get('request_new_link', 'Request a new link', 'resetPassword.requestNewLink')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (success) {
    return (
      <>
        <SEO page="resetPassword" />
        <div className="min-h-screen bg-gray-50">
          {/* Hero Section */}
          <div className="relative overflow-hidden bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl opacity-10" />
              <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-accent rounded-full blur-3xl opacity-15" />
            </div>
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-32 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium text-white mb-6 border border-white/20 animate-fade-in">
                <CheckCircle className="w-4 h-4" />
                {t('resetPassword.successBadge', 'Password Reset')}
              </div>
              <EditableElement section="auth" field="reset_success_title">
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white animate-slide-up">
                  {get('reset_success_title', 'Password reset', 'resetPassword.successTitle')}{' '}
                  <span className="text-accent">{get('reset_success_title_highlight', 'successful', 'resetPassword.successTitleHighlight')}</span>
                </h1>
              </EditableElement>
              <EditableElement section="auth" field="reset_success_message">
                <p className="mt-4 text-lg text-primary-100 max-w-2xl mx-auto animate-slide-up animation-delay-100">
                  {get('reset_success_message', 'Your password has been reset. You can now log in with your new password.', 'resetPassword.successMessage')}
                </p>
              </EditableElement>
            </div>
            <div className="absolute bottom-0 left-0 right-0">
              <svg viewBox="0 0 1440 80" fill="none" className="w-full h-auto">
                <path d="M0 80L60 70C120 60 240 40 360 35C480 30 600 40 720 45C840 50 960 50 1080 45C1200 40 1320 30 1380 25L1440 20V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0Z" fill="#f9fafb" />
              </svg>
            </div>
          </div>

          {/* Card */}
          <div className="relative -mt-24 pb-16">
            <div className="max-w-md mx-auto px-4">
              <div className="bg-white rounded-2xl shadow-2xl p-8 text-center animate-fade-in animation-delay-200">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <p className="mt-4 text-gray-600">
                  {get('reset_success_help', 'Click below to sign in with your new password.', 'resetPassword.successHelp')}
                </p>
                <button
                  onClick={() => navigate('/login')}
                  className="mt-6 inline-block px-6 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors"
                >
                  {get('go_to_login', 'Go to login', 'resetPassword.goToLogin')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO page="resetPassword" />
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl opacity-10" />
            <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-accent rounded-full blur-3xl opacity-15" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-32 text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium text-white mb-6 border border-white/20 animate-fade-in">
              <KeyRound className="w-4 h-4" />
              {t('resetPassword.badge', 'Reset Password')}
            </div>

            {/* Title */}
            <EditableElement section="auth" field="reset_title">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white animate-slide-up">
                {get('reset_title', 'Create a new', 'resetPassword.heroTitle')}{' '}
                <span className="text-accent">{get('reset_title_highlight', 'password', 'resetPassword.heroTitleHighlight')}</span>
              </h1>
            </EditableElement>

            {/* Subtitle */}
            <EditableElement section="auth" field="reset_subtitle">
              <p className="mt-4 text-lg text-primary-100 max-w-2xl mx-auto animate-slide-up animation-delay-100">
                {get('reset_subtitle', 'Enter your new password below to regain access to your account.', 'resetPassword.heroSubtitle')}
              </p>
            </EditableElement>
          </div>

          {/* Wave divider */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1440 80" fill="none" className="w-full h-auto">
              <path d="M0 80L60 70C120 60 240 40 360 35C480 30 600 40 720 45C840 50 960 50 1080 45C1200 40 1320 30 1380 25L1440 20V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0Z" fill="#f9fafb" />
            </svg>
          </div>
        </div>

        {/* Form Card */}
        <div className="relative -mt-24 pb-16">
          <div className="max-w-md mx-auto px-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 animate-fade-in animation-delay-200">
              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Error message */}
                {error && (
                  <div role="alert" className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                    <span className="text-sm">{error}</span>
                  </div>
                )}

                {/* New Password */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                    {get('new_password_label', 'New password', 'resetPassword.newPasswordLabel')}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" aria-hidden="true" />
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                      className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                      placeholder={get('new_password_placeholder', 'Enter new password', 'resetPassword.newPasswordPlaceholder')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label={showPassword ? t('auth.hidePassword', 'Hide password') : t('auth.showPassword', 'Show password')}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>

                  {/* Password requirements */}
                  {password && (
                    <div className="mt-3 space-y-1">
                      {passwordRequirements.map((req, index) => (
                        <div
                          key={index}
                          className={`flex items-center gap-2 text-xs ${
                            req.met ? 'text-green-600' : 'text-gray-500'
                          }`}
                        >
                          <div
                            className={`w-4 h-4 rounded-full flex items-center justify-center ${
                              req.met ? 'bg-green-100' : 'bg-gray-100'
                            }`}
                          >
                            {req.met && <Check className="w-3 h-3" />}
                          </div>
                          {req.text}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    {get('confirm_password_label', 'Confirm new password', 'resetPassword.confirmPasswordLabel')}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" aria-hidden="true" />
                    <input
                      id="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                      className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-600 focus:border-transparent ${
                        confirmPassword && !passwordsMatch ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder={get('confirm_password_placeholder', 'Confirm new password', 'resetPassword.confirmPasswordPlaceholder')}
                    />
                  </div>
                  {confirmPassword && !passwordsMatch && (
                    <p className="mt-1 text-xs text-red-600">{get('error_passwords_not_match', 'Passwords do not match', 'resetPassword.errorPasswordsNotMatch')}</p>
                  )}
                </div>

                {/* Turnstile */}
                {turnstileSiteKey && (
                  <Turnstile
                    siteKey={turnstileSiteKey}
                    onVerify={setTurnstileToken}
                  />
                )}

                {/* Submit button */}
                <button
                  type="submit"
                  disabled={isLoading || !allRequirementsMet || !passwordsMatch || (!!turnstileSiteKey && !turnstileToken)}
                  className="w-full py-3 px-4 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 focus:ring-4 focus:ring-primary-600/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? get('resetting', 'Resetting password...', 'resetPassword.resetting') : get('reset_button', 'Reset password', 'resetPassword.resetButton')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
